import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown, LayoutDashboard, LogOut, Menu, UserRound, X } from "lucide-react";

const TAB_BAR_ROUTES = ["/", "/services", "/about", "/contact", "/dashboard", "/worker", "/admin"];

export default function Navbar() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const hasTabBar = TAB_BAR_ROUTES.includes(loc.pathname);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const dashHref =
    user?.role === "admin" ? "/admin" :
    user?.role === "worker" ? "/worker" :
    user?.role === "user" ? "/dashboard" : "/login";

  const linkClass = (to) => {
    const active = loc.pathname === to;
    return `relative px-1 py-2 text-sm font-semibold tracking-wide transition-colors after:absolute after:left-1 after:right-1 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-[#D9734E] after:transition-transform ${
      active
        ? "text-[#1C4532] after:scale-x-100"
        : "text-[#646964] after:scale-x-0 hover:text-[#1A1D1A] hover:after:scale-x-100"
    }`;
  };

  const mobileLinkClass = (to) => {
    const active = loc.pathname === to;
    return `rounded-sm px-3 py-3 text-sm font-semibold transition-colors ${
      active
        ? "bg-[#F3F1ED] text-[#1C4532]"
        : "text-[#1A1D1A] hover:bg-[#F8F6F1] hover:text-[#1C4532]"
    }`;
  };

  const handleLogout = () => {
    setLogoutOpen(true);
  };

  const confirmLogout = () => {
    logout();
    nav("/");
    setOpen(false);
    setLogoutOpen(false);
  };

  return (
    <header className={`sticky top-0 z-40 border-b border-[#E2E0D9]/80 bg-[#FAF9F6]/92 shadow-[0_10px_30px_rgba(28,69,50,0.06)] backdrop-blur-xl ${hasTabBar ? "hide-on-mobile-tab" : ""}`}>
      <div className="mx-auto flex min-h-[82px] max-w-7xl items-center justify-between px-6 py-3 sm:px-8 lg:px-14">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-3 font-display text-lg font-semibold tracking-tight text-[#1A1D1A]"
        >
          <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-[#050706] shadow-[0_8px_20px_rgba(28,69,50,0.18)] transition-transform group-hover:-translate-y-0.5">
            <img src="/theuniversaltree-logo.jpeg" alt="Universal Tree logo" className="h-full w-full object-cover rounded-full" />
          </span>
          <span className="leading-none">Universal Tree</span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={linkClass(l.to)}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {loading ? (
            <div className="h-9 w-28 animate-pulse rounded-sm bg-[#F3F1ED]"/>
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="nav-profile"
                  className="rounded-sm border-[#1C4532]/25 bg-white/50 px-4 text-[#1C4532] shadow-sm hover:border-[#1C4532] hover:bg-[#F3F1ED]"
                >
                  <span className="grid h-6 w-6 place-items-center rounded-sm bg-[#1C4532] text-white">
                    <UserRound size={14} />
                  </span>
                    {t("nav.profile")}
                   <ChevronDown size={15} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-56 rounded-sm border-[#E2E0D9] bg-white p-2 shadow-[0_18px_40px_rgba(28,69,50,0.14)]"
              >
                <DropdownMenuLabel className="px-3 py-2">
                  <span className="block text-sm font-semibold text-[#1A1D1A]">{user.name || t("nav.profile")}</span>
                  <span className="block truncate text-xs font-normal text-[#646964]">{user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-2 bg-[#E2E0D9]" />
                <DropdownMenuItem
                  onSelect={() => nav(dashHref)}
                  data-testid="nav-dashboard"
                  className="cursor-pointer rounded-sm px-3 py-2.5 text-[#1A1D1A] focus:bg-[#F3F1ED] focus:text-[#1C4532]"
                >
                  <LayoutDashboard size={16} />
                  {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleLogout}
                  data-testid="nav-logout"
                  className="cursor-pointer rounded-sm px-3 py-2.5 text-[#D9734E] focus:bg-[#FFF3ED] focus:text-[#D9734E]"
                >
                  <LogOut size={16} />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                        <Button variant="ghost" className="rounded-sm px-4 text-[#1A1D1A] hover:bg-[#F3F1ED] hover:text-[#1C4532]">{t("nav.login")}</Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                        <Button className="rounded-sm bg-[#1C4532] px-5 text-white shadow-[0_10px_22px_rgba(28,69,50,0.16)] hover:bg-[#143324]">{t("nav.signup")}</Button>
              </Link>
            </>
          )}
        </div>

        <button
          className={`grid h-10 w-10 place-items-center rounded-sm border border-[#E2E0D9] bg-white/55 text-[#1A1D1A] shadow-sm transition-colors hover:bg-[#F3F1ED] md:hidden ${hasTabBar ? "hidden" : ""}`}
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label={t("nav.toggle")}
          aria-expanded={open}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className={`border-t border-[#E2E0D9] bg-[#FAF9F6]/98 px-5 py-4 shadow-[0_18px_35px_rgba(28,69,50,0.08)] md:hidden ${hasTabBar ? "hidden" : ""}`}>
          <div className="mx-auto flex max-w-7xl flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={mobileLinkClass(l.to)}>
              {l.label}
            </Link>
          ))}
          <div className="my-3 h-px bg-[#E2E0D9]" />
          {user ? (
            <>
              <div className="rounded-sm border border-[#E2E0D9] bg-white/60 p-2">
                <div className="flex items-center gap-3 px-2 py-2">
                  <span className="grid h-8 w-8 place-items-center rounded-sm bg-[#1C4532] text-white">
                    <UserRound size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#1A1D1A]">{user.name || t("nav.profile")}</p>
                    <p className="truncate text-xs text-[#646964]">{user.email}</p>
                  </div>
                </div>
                <div className="my-2 h-px bg-[#E2E0D9]" />
                <Link to={dashHref} onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-sm px-2 py-3 text-sm font-semibold text-[#1C4532] hover:bg-[#F3F1ED]">
                  <LayoutDashboard size={16} />
                  {t("nav.dashboard")}
                </Link>
                <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-sm px-2 py-3 text-left text-sm font-semibold text-[#D9734E] hover:bg-[#F8F6F1]">
                  <LogOut size={16} />
                  {t("nav.logout")}
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="rounded-sm px-3 py-3 text-sm font-semibold text-[#1A1D1A] hover:bg-[#F3F1ED]">{t("nav.login")}</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="mt-2 rounded-sm bg-[#1C4532] px-3 py-3 text-center text-sm font-semibold text-white shadow-[0_10px_22px_rgba(28,69,50,0.16)]">{t("nav.signup")}</Link>
            </>
          )}
          </div>
        </div>
      )}

      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent className="rounded-xl max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg text-[#1A1D1A]">Log out</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#646964]">
              Are you sure you want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:flex-row">
            <AlertDialogCancel className="rounded-lg h-11 text-sm flex-1">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLogout} className="rounded-lg h-11 text-sm bg-[#D9734E] hover:bg-[#C26240] text-white flex-1">
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
